import { useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { Ambulance, Check, ChevronDown, ChevronUp, Phone, PhoneOff, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useDecideDispatch,
  useEmergencyDispatches,
  type EmergencyDispatch,
} from "@/shared/api/hooks/emergency";
import { useSites } from "@/shared/api/hooks/sites";
import { qk } from "@/shared/api/keys";
import { useOrgAlertChannel } from "@/shared/api/ws";
import { useFeature } from "@/shared/auth/features";
import { selectUser, useAuth } from "@/shared/auth/store";
import { hasMinRole } from "@/shared/auth/permissions";
import { cn } from "@/shared/lib/cn";
import { useAlarmOverlayStore } from "@/shared/state/alarmOverlayStore";
import { mixOr } from "@/shared/lib/cssSupport";

/**
 * 119 신고 확인 — 심각 경보가 나면 관제요원에게 「119 에 직접 신고하고 기록하라」고 요구하는 표면.
 *
 * **이 시스템은 119 에 전화하지 않는다.** 백엔드 어댑터(`emergency_call.py`)는 모의 전용이고
 * live 발신 코드가 없으며, 앱에는 CALL_PHONE 권한조차 없다. 예전 문구(「119 발신 승인」·
 * 「발신 중…」)는 시스템이 거는 것처럼 읽혀 2026-09-11 에 사실대로 고쳤다 — 버튼은
 * 관제요원이 **직접 신고한 사실을 기록**하는 것이고, 카드에는 그 사실을 한 줄로 못박는다.
 *
 * 백엔드가 게이트를 열면(`emergency.gate.opened`) 카운트다운(1초 감소) 안에 기록해야 한다.
 * 미기록 만료는 서버 스윕이 미신고로 정리한다.
 *
 * **배치 (2026-09-04 재설계).** 예전에는 오른쪽 고정 도크(380px)였다. 폰에서는 경보
 * 오버레이(z-90)가 도크(z-85)를 **통째로 덮어 승인 버튼을 누를 수 없었고**, 1920 보드에서는
 * 도크가 실시간 경보 패널 위에 걸쳐 어긋나 보였다. 지금은 표면이 하나뿐이다:
 *   · 경보 오버레이가 떠 있으면 → 오버레이 **맨 위 배너**(`DispatchBanner`). 겹칠 것이 없다.
 *   · 오버레이가 닫혀 있으면 → 화면 **아래 떠 있는 승인 바**(폰은 전폭, 데스크톱은 우하단 카드).
 * 둘 다 같은 `DispatchPanel` 이라 버튼·카운트다운·문구가 하나다.
 *
 * **조작은 두 번 누를 것 없이 한 번.** 「119 신고 완료」(빨강, 큰 버튼)와 「신고 안 함」.
 * 신고 안 함은 미신고·이력만 남기므로 그냥 두어 만료되는 것과 결과가 같다 — 확인 단계를
 * 두지 않는다. 대기 건이 여럿이면 가장 급한 건을 크게, 나머지는 칩으로 바꿔 볼 수 있다.
 *
 * **접을 수 있다.** 닫기(X)가 없어 화면을 계속 가린다는 지적이 있었다. 결정은 남겨야 하므로
 * 없애지 않고 **작은 알약**으로 접는다 — 현장·남은 시간만 보이고, 누르면 다시 펼쳐진다.
 * 접힘 상태는 건(id)별로 모듈 수준에 두어 배너↔바 전환·리마운트에도 유지된다(선택 건은
 * 표면마다 따로라 — `useDispatchQueue` 의 로컬 state — 전환하면 가장 급한 건으로 돌아간다).
 *
 * 승인·보류는 사이트관리자 이상만(08 §3, D-R6). 사용자(operator)·작업자에게는 안 띄운다.
 */

/** 승인/보류 처리한(또는 처리 중인) 건 — 목록 갱신 전까지 숨긴다. 리마운트에도 남게 모듈 수준. */
const decidedIds = new Set<number>();
/** 관제요원이 접어 둔 건 — 새 건은 펼쳐진 채로 뜬다. 건이 목록에서 빠지면 함께 비운다. */
const collapsedIds = new Set<number>();

export interface DispatchQueue {
  pending: EmergencyDispatch[];
  selected: EmergencyDispatch | null;
  select: (id: number) => void;
  siteName: (id: number) => string;
}

/**
 * 대기 중 119 게이트 목록 + 선택. 관제 권한이 없거나 조직이 119 신고 대응(`dispatch_119`)을 껐으면
 * 항상 비어 있고 폴링도 하지 않는다 — 빈 큐면 승인 바(`EmergencyDispatchCenter`)와 경보창 배너
 * (`DispatchBanner`)가 둘 다 그리지 않는다(2026-09-30 「구현됨·현장 미사용」).
 */
export function useDispatchQueue(): DispatchQueue {
  const { t } = useTranslation();
  const user = useAuth(selectUser);
  const on119 = useFeature("dispatch_119");
  const active = hasMinRole(user?.role, "safety_manager") && on119;
  const { data } = useEmergencyDispatches(undefined, { enabled: active });
  const { data: sitesData } = useSites();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [, bump] = useState(0);

  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    (sitesData?.results ?? []).forEach((s) => s.id != null && m.set(s.id, s.name));
    return (id: number) => m.get(id) ?? t("widgets.emergency.siteFallback", { id });
  }, [sitesData, t]);

  // 마감이 임박한 순서 — 가장 급한 건이 기본 선택.
  const pending = useMemo(
    () =>
      active
        ? (data?.results ?? [])
            .filter((d) => d.state === "pending" && !decidedIds.has(d.id))
            .sort((a, b) => +new Date(a.countdown_deadline) - +new Date(b.countdown_deadline))
        : [],
    [data, active],
  );

  // 처리한 건이 서버 목록에서 빠지면 숨김 집합도 비운다(무한히 자라지 않게).
  useEffect(() => {
    const ids = new Set((data?.results ?? []).map((d) => d.id));
    let changed = false;
    decidedIds.forEach((id) => {
      if (!ids.has(id)) { decidedIds.delete(id); changed = true; }
    });
    collapsedIds.forEach((id) => {
      if (!ids.has(id)) { collapsedIds.delete(id); changed = true; }
    });
    if (changed) bump((n) => n + 1);
  }, [data]);

  const selected = pending.find((d) => d.id === selectedId) ?? pending[0] ?? null;
  return { pending, selected, select: setSelectedId, siteName };
}

/** 항상 마운트(AppShell) — WS 로 목록을 즉시 갱신하고, 오버레이가 닫혀 있을 때만 떠 있는 승인 바를 그린다. */
export function EmergencyDispatchCenter() {
  const qc = useQueryClient();
  const user = useAuth(selectUser);
  const orgId = user?.organization?.id ?? null;
  const overlayOpen = useAlarmOverlayStore((s) => s.open);
  const queue = useDispatchQueue();

  useOrgAlertChannel(orgId, (env) => {
    if (env.type === "emergency.gate.opened" || env.type === "emergency.gate.decided") {
      qc.invalidateQueries({ queryKey: qk.safety.emergency.all });
    }
  });

  if (overlayOpen || !queue.selected) return null;
  return <DispatchPanel queue={queue} variant="bar" />;
}

/** 경보 오버레이 맨 위에 끼우는 배너 — 대기 건이 없으면 null. */
export function DispatchBanner() {
  const queue = useDispatchQueue();
  if (!queue.selected) return null;
  return <DispatchPanel queue={queue} variant="banner" />;
}

/**
 * 확인 표면 본체. `banner` = 오버레이 안 전폭 띠, `bar` = 화면 아래 떠 있는 카드.
 * 내용은 같다: 무엇(현장·가스·RI) · 얼마나 남았나 · 「전화하지 않는다」 고지 · 신고 완료/안 함 ·
 * 다음 대기 칩. 접혀 있으면 알약 하나만 그린다.
 */
export function DispatchPanel({
  queue,
  variant,
}: {
  queue: DispatchQueue;
  variant: "banner" | "bar";
}) {
  const { t } = useTranslation();
  const decide = useDecideDispatch();
  const [, rerender] = useState(0);
  const { pending, selected, select, siteName } = queue;
  const remaining = useCountdown(selected?.countdown_deadline ?? new Date().toISOString());
  // 접기/펼치기는 다른 요소를 마운트하므로 포커스가 body 로 떨어진다 — 사용자가 직접 눌렀을 때만
  // 새 컨트롤로 옮긴다(게이트가 새로 열려 마운트될 때는 훔치지 않는다).
  const pillRef = useRef<HTMLButtonElement>(null);
  const collapseRef = useRef<HTMLButtonElement>(null);
  const focusPending = useRef(false);
  useEffect(() => {
    if (!focusPending.current) return;
    focusPending.current = false;
    (pillRef.current ?? collapseRef.current)?.focus();
  });
  if (!selected) return null;

  const d = selected;
  const call = d.payload?.call_119 ?? {};
  const expired = remaining <= 0;
  const urgent = remaining <= 30;
  const busy = decide.isPending;
  const collapsed = collapsedIds.has(d.id);
  const setCollapsed = (v: boolean) => {
    if (v) collapsedIds.add(d.id); else collapsedIds.delete(d.id);
    focusPending.current = true;
    rerender((n) => n + 1);
  };

  const run = (decision: "approve" | "reject") =>
    decide.mutate(
      { id: d.id, decision },
      { onSuccess: () => { decidedIds.add(d.id); select(0); } },
    );

  const others = pending.filter((p) => p.id !== d.id);
  const bar = variant === "bar";

  // 접힘 — 결정은 남긴 채 자리만 줄인다. 누르면 펼쳐진다.
  if (collapsed) {
    return (
      <button
        ref={pillRef}
        type="button"
        onClick={() => setCollapsed(false)}
        aria-expanded={false}
        title={t("widgets.emergency.collapsedAria")}
        className={cn(
          // 접근 가능한 이름 = 보이는 텍스트(현장·남은 시간). 힌트는 title 로만 — aria-label 로
          // 덮으면 스크린리더가 어느 현장인지 못 듣는다.
          "flex items-center gap-2 text-[13px] font-semibold text-[var(--fg)]",
          bar
            ? "fixed z-[85] h-11 max-w-[calc(100vw-1rem)] rounded-full border-2 pl-1.5 pr-3.5 shadow-[var(--shadow-3,0_12px_32px_rgba(0,0,0,.28))] bottom-[calc(var(--tabbar-h)+0.5rem)] right-2 lg:bottom-4 lg:right-4"
            : "w-full border-b-2 px-4 py-2 sm:px-6",
        )}
        style={{
          borderColor: "var(--danger, #ef4444)",
          background: bar
            ? "var(--bg-elevated, var(--bg))"
            : mixOr("color-mix(in oklab, var(--danger,#ef4444) 10%, var(--bg-elevated))", "var(--danger-soft)"),
        }}
      >
        <span
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-white"
          style={{ background: "var(--danger, #ef4444)" }}
        >
          <Ambulance size={16} strokeWidth={2.1} />
        </span>
        <span className="truncate">
          {t("widgets.emergency.dockTitle")} · {siteName(d.site)}
          {pending.length > 1 ? ` +${pending.length - 1}` : ""}
        </span>
        <span className="tabular-nums" style={{ color: urgent ? "var(--danger,#ef4444)" : "var(--fg-muted)" }}>
          {mmss(remaining)}
        </span>
        <ChevronUp size={16} className="shrink-0 text-[var(--fg-subtle)]" />
      </button>
    );
  }

  return (
    <section
      role={bar ? "region" : undefined}
      aria-label={t("widgets.emergency.barAria")}
      className={cn(
        "text-[var(--fg)]",
        bar
          ? cn(
              // 폰: 탭바 위 전폭. 데스크톱: 우하단 카드. z-85 = 경보 오버레이(z-90) 아래지만
              // 오버레이가 열리면 이 바는 그려지지 않으므로 실제로 겹치는 일은 없다.
              "fixed inset-x-2 z-[85] rounded-2xl border-2 shadow-[var(--shadow-3,0_12px_32px_rgba(0,0,0,.28))]",
              "bottom-[calc(var(--tabbar-h)+0.5rem)] lg:inset-x-auto lg:bottom-4 lg:right-4 lg:w-[560px]",
            )
          : "border-b-2",
      )}
      style={{
        borderColor: "var(--danger, #ef4444)",
        background: bar
          ? "var(--bg-elevated, var(--bg))"
          : mixOr("color-mix(in oklab, var(--danger,#ef4444) 10%, var(--bg-elevated))", "var(--danger-soft)"),
        animation: "fade-in var(--motion-fast, 140ms) var(--ease-out-soft, ease) both",
      }}
    >
      <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-2.5", bar ? "p-3.5" : "px-4 py-3 sm:px-6")}>
        {/* 무엇 */}
        <span
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-white"
          style={{ background: "var(--danger, #ef4444)" }}
        >
          <Ambulance size={20} strokeWidth={2.1} />
        </span>
        <div className="min-w-0 flex-1 basis-[200px]">
          <div className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-[var(--danger,#ef4444)]">
            {t("widgets.emergency.dockTitle")}
            {pending.length > 1 ? ` · ${t("widgets.emergency.waitingCount", { n: pending.length })}` : ""}
          </div>
          <div className="truncate text-[16px] font-bold leading-tight">
            {siteName(d.site)} · {gasLabel(t, d.gas)}
          </div>
          <div className="mt-0.5 text-[12.5px] text-[var(--fg-muted)]">
            {t("widgets.emergency.ri")}{" "}
            <b className="tabular-nums" style={{ color: "var(--danger,#ef4444)" }}>{d.ri_at_open.toFixed(2)}</b>
            {call.concentration ? ` · ${t("widgets.emergency.concentration")} ${call.concentration}` : ""}
          </div>
        </div>

        {/* 얼마나 남았나 */}
        <div className="shrink-0 text-right">
          <div className="text-[11px] font-medium text-[var(--fg-subtle)]">{t("widgets.emergency.remaining")}</div>
          <div
            className="rounded-lg px-2.5 py-0.5 text-[26px] font-bold leading-tight tabular-nums"
            style={{
              color: urgent ? "var(--danger,#ef4444)" : "var(--fg)",
              background: urgent ? mixOr("color-mix(in oklab, var(--danger,#ef4444) 16%, transparent)", "var(--danger-soft)") : "transparent",
            }}
          >
            {mmss(remaining)}
          </div>
        </div>

        {/* 접기 — 화면을 덜 가리게. 결정은 사라지지 않는다. */}
        <button
          ref={collapseRef}
          type="button"
          onClick={() => setCollapsed(true)}
          aria-expanded={true}
          aria-label={t("widgets.emergency.collapse")}
          title={t("widgets.emergency.collapse")}
          className="ml-auto grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[var(--fg-subtle)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]"
        >
          <ChevronDown size={18} strokeWidth={2.25} />
        </button>

        {/* 사실 고지 — 이 시스템은 전화하지 않는다. 버튼 위에, 항상. */}
        <p className="flex w-full basis-full items-start gap-1.5 text-[12.5px] leading-snug text-[var(--fg-muted)]">
          <PhoneOff size={14} className="mt-[2px] shrink-0 text-[var(--danger,#ef4444)]" />
          <span>{t("widgets.emergency.notice")}</span>
        </p>

        {/* 한 번 누르면 끝 — 큰 버튼 둘. 폰에서는 한 줄 전폭. */}
        <div className="flex w-full basis-full gap-2 sm:w-auto sm:basis-auto">
          <button
            type="button"
            onClick={() => run("reject")}
            disabled={busy}
            className="flex h-12 flex-1 items-center justify-center gap-1.5 rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] px-4 text-[14.5px] font-semibold text-[var(--fg-muted)] transition-colors hover:bg-[var(--bg-hover)] disabled:opacity-60 sm:min-w-[132px]"
          >
            <X size={17} strokeWidth={2.25} /> {t("widgets.emergency.skip")}
          </button>
          <button
            type="button"
            onClick={() => run("approve")}
            disabled={busy || expired}
            className="flex h-12 flex-[1.4] items-center justify-center gap-1.5 rounded-xl px-4 text-[15px] font-bold text-white transition-transform active:scale-[0.98] disabled:opacity-60 sm:min-w-[184px]"
            style={{ background: "var(--danger, #ef4444)" }}
          >
            <Check size={18} strokeWidth={2.5} />
            {busy ? t("widgets.emergency.dispatching") : t("widgets.emergency.approve")}
          </button>
        </div>
      </div>

      {/* 신고 대본(관제요원이 119 에 읽어줄 내용)·오류·만료 — 필요할 때만 */}
      {(call.tts_script || decide.isError || expired) && (
        <div className={cn("space-y-1 pb-3", bar ? "px-3.5" : "px-4 sm:px-6")}>
          {call.tts_script && (
            <p className="line-clamp-3 text-[12.5px] leading-relaxed text-[var(--fg-muted)]">
              <Phone size={12} className="mr-1 inline align-[-1px]" />
              <b className="font-semibold text-[var(--fg)]">{t("widgets.emergency.scriptLabel")}:</b>{" "}
              {call.tts_script}
            </p>
          )}
          {decide.isError && (
            <p className="text-[12px] text-[var(--danger,#ef4444)]">{t("widgets.emergency.decideFailed")}</p>
          )}
          {expired && <p className="text-[12px] text-[var(--fg-subtle)]">{t("widgets.emergency.expired")}</p>}
        </div>
      )}

      {/* 다음 대기 건 — 칩. 누르면 위가 그 건으로 바뀐다. */}
      {others.length > 0 && (
        <div className={cn("flex items-center gap-1.5 overflow-x-auto pb-3 [scrollbar-width:none]", bar ? "px-3.5" : "px-4 sm:px-6")}>
          <span className="shrink-0 text-[11.5px] text-[var(--fg-subtle)]">
            {t("widgets.emergency.nextN", { n: others.length })}
          </span>
          {others.map((p) => (
            <DispatchChip key={p.id} dispatch={p} siteName={siteName(p.site)} onSelect={() => select(p.id)} />
          ))}
        </div>
      )}
    </section>
  );
}

function DispatchChip({
  dispatch,
  siteName,
  onSelect,
}: {
  dispatch: EmergencyDispatch;
  siteName: string;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const remaining = useCountdown(dispatch.countdown_deadline);
  const urgent = remaining <= 30;
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex shrink-0 items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--bg-elevated)] px-2.5 py-1 text-[12px] hover:bg-[var(--bg-hover)]"
    >
      <span className="font-bold tabular-nums" style={{ color: urgent ? "var(--danger,#ef4444)" : "var(--fg-muted)" }}>
        {mmss(remaining)}
      </span>
      <span className="max-w-[140px] truncate font-medium">{siteName}</span>
      <span className="text-[var(--fg-subtle)]">{gasLabel(t, dispatch.gas)}</span>
    </button>
  );
}

/** 남은 초 → `m:ss` (음수는 0:00). */
function mmss(seconds: number): string {
  const s = Math.max(0, seconds);
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
}

/** 남은 초를 1초마다 실시간 감소. */
function useCountdown(deadlineIso: string): number {
  const deadline = useMemo(() => new Date(deadlineIso).getTime(), [deadlineIso]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return Math.round((deadline - now) / 1000);
}

/** 현지화된 가스명을 가진 코드 — 라벨은 렌더 시점에 t() 로 조회한다. */
const GAS_CODES = ["CO", "H2S", "NH3", "CH4", "CO2", "O2", "SO2", "NO2", "VOC"];
function gasLabel(t: TFunction, code: string): string {
  if (!code) return t("widgets.emergency.gas.composite");
  const c = code.toUpperCase();
  return GAS_CODES.includes(c) ? t(`widgets.emergency.gas.${c}`) : code;
}
